"use strict";
import { useRecentConnectorId } from "components/Web3Provider/constants";
import { useConnect } from "hooks/useConnect";
import { useCallback, useMemo } from "react";
import { CONNECTION_PROVIDER_IDS } from "constants/web3";
import { isPlaywrightEnv } from "utilities/src/environment/env";
import { isMobileWeb } from "utilities/src/platform";
const SHOULD_THROW = { shouldThrow: true };
const UNISWAP_EXTENSION_RDNS = "org.uniswap.app";
function getConnectorWithId(connectors, id, options) {
  const connector = connectors.find((c) => c.id === id);
  if (!connector && options?.shouldThrow) {
    throw new Error(`Expected connector ${id} missing from wagmi context.`);
  }
  return connector;
}
export function useConnectorWithId(id, options) {
  const { connectors } = useConnect();
  return useMemo(
    () => options?.shouldThrow ? getConnectorWithId(connectors, id, options) : getConnectorWithId(connectors, id),
    [connectors, id, options]
  );
}
function getInjectedConnectors(connectors) {
  let isCoinbaseWalletBrowser = false;
  const injectedConnectors = connectors.filter((c) => {
    if (c.id === CONNECTION_PROVIDER_IDS.COINBASE_RDNS) {
      if (isMobileWeb) {
        isCoinbaseWalletBrowser = true;
      }
      return false;
    }
    if (c.id === UNISWAP_EXTENSION_RDNS) {
      return false;
    }
    if (c.id === CONNECTION_PROVIDER_IDS.BINANCE_W3W_CONNECTOR_ID) {
      return false;
    }
    return c.type === CONNECTION_PROVIDER_IDS.INJECTED_CONNECTOR_TYPE;
  });
  return { injectedConnectors, isCoinbaseWalletBrowser };
}
export function useOrderedConnections() {
  const { connectors } = useConnect();
  const recentConnectorId = useRecentConnectorId();
  const sortByRecent = useCallback(
    (a, b) => {
      if (a.id === recentConnectorId) {
        return -1;
      } else if (b.id === recentConnectorId) {
        return 1;
      } else {
        return 0;
      }
    },
    [recentConnectorId]
  );
  return useMemo(() => {
    const { injectedConnectors: injectedConnectorsBase, isCoinbaseWalletBrowser } = getInjectedConnectors(connectors);
    const injectedConnectors = injectedConnectorsBase.map((c) => ({ ...c, isInjected: true }));
    const walletConnectConnector = getConnectorWithId(
      connectors,
      CONNECTION_PROVIDER_IDS.WALLET_CONNECT_CONNECTOR_ID,
      SHOULD_THROW
    );
    if (!walletConnectConnector) {
      throw new Error("Expected walletConnect connector missing from wagmi context.");
    }
    if (isPlaywrightEnv()) {
      const mockConnector = getConnectorWithId(connectors, CONNECTION_PROVIDER_IDS.MOCK_CONNECTOR_ID, SHOULD_THROW);
      return [mockConnector];
    }
    if (isCoinbaseWalletBrowser) {
      const coinbaseConnector = getConnectorWithId(
        connectors,
        CONNECTION_PROVIDER_IDS.COINBASE_SDK_CONNECTOR_ID,
        SHOULD_THROW
      );
      return [coinbaseConnector];
    }
    const orderedConnectors = [];
    const sortedInjectedConnectors = injectedConnectors.sort((a, b) => {
      if (a.id === CONNECTION_PROVIDER_IDS.METAMASK_RDNS) return -1;
      if (b.id === CONNECTION_PROVIDER_IDS.METAMASK_RDNS) return 1;
      return 0;
    });
    orderedConnectors.push(...sortedInjectedConnectors);
    orderedConnectors.push(walletConnectConnector);
    return orderedConnectors.sort(sortByRecent);
  }, [connectors, recentConnectorId, sortByRecent]);
}
